// =============================================
// MusicNowPlaying — full-page player (Play All / open-a-playlist)
// =============================================
// The rich player UI from the mockup: art, title, repeat/prev/play/next/shuffle,
// like/dislike/favorite/help/queue/volume, a seek bar, and the queue (the playlist
// or collection) listed underneath. Bound to the shared useMusicPlayer hook (the
// audio element lives in MusicPage, so playback persists when you leave this page).
// =============================================
import React, { useEffect, useState } from 'react';
import {
  FiHome, FiArrowLeft, FiMenu, FiRepeat, FiSkipBack, FiSkipForward, FiShuffle,
  FiPlay, FiPause, FiThumbsUp, FiThumbsDown, FiHeart, FiHelpCircle, FiList,
  FiVolume2, FiVolumeX, FiMusic,
} from 'react-icons/fi';
import { TrackView, setRating, setFavorite } from './musicLibrary';
import { getAudioUrl } from '../../lib/audioStore';
import type { MusicPlayerApi } from './useMusicPlayer';

interface Props {
  player: MusicPlayerApi;
  queue: TrackView[];
  index: number;
  setIndex: (i: number) => void;
  title: string;
  onBack: () => void;
  onHome: () => void;
  onChanged: () => void;
}

function fmtTime(sec: number): string {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60), s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const MusicNowPlaying: React.FC<Props> = ({ player, queue, index, setIndex, title, onBack, onHome, onChanged }) => {
  const [showList, setShowList] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  // Optimistic per-track like/fav overrides so taps feel instant (DB write + refresh follow).
  const [ovr, setOvr] = useState<Record<number, { rating?: number; favorite?: boolean }>>({});

  const track = player.track;

  // Resolve the current track's cover art (IndexedDB blob → object URL, revoked on change).
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setCoverUrl(null);
    if (track?.coverKey) {
      getAudioUrl(track.coverKey).then(u => {
        if (cancelled) { if (u) URL.revokeObjectURL(u); return; }
        url = u; setCoverUrl(u);
      });
    }
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [track?.coverKey]);

  const ratingOf = (t: TrackView) => ovr[t.trackId]?.rating ?? t.rating;
  const favOf = (t: TrackView) => ovr[t.trackId]?.favorite ?? t.isFavorite;

  const like = async (t: TrackView) => {
    const next = ratingOf(t) === 5 ? 0 : 5;
    setOvr(o => ({ ...o, [t.trackId]: { ...o[t.trackId], rating: next } }));
    await setRating(t.userOwnedId, next);
    onChanged();
  };
  const dislike = async (t: TrackView) => {
    const next = ratingOf(t) === 1 ? 0 : 1;
    setOvr(o => ({ ...o, [t.trackId]: { ...o[t.trackId], rating: next } }));
    await setRating(t.userOwnedId, next);
    onChanged();
  };
  const fav = async (t: TrackView) => {
    const next = !favOf(t);
    setOvr(o => ({ ...o, [t.trackId]: { ...o[t.trackId], favorite: next } }));
    await setFavorite(t.userOwnedId, next);
    onChanged();
  };

  const VolIcon = player.muted || player.volume === 0 ? FiVolumeX : FiVolume2;

  return (
    <div className="mnp">
      {/* Top bar */}
      <div className="mnp-top">
        <div className="mnp-top-left">
          <button className="mnp-icon-btn" onClick={onHome} title="Library home"><FiHome /></button>
          <button className="mnp-icon-btn" onClick={onBack} title="Back"><FiArrowLeft /></button>
        </div>
        <div className="mnp-now">Now playing:</div>
        <button className="mnp-icon-btn" onClick={() => setShowList(s => !s)} title="Toggle list"><FiMenu /></button>
      </div>

      {/* Title */}
      <h2 className="mnp-title">{track ? track.title : 'Nothing playing'}</h2>
      <p className="mnp-sub">{track ? `${track.artist}${track.album ? ' · ' + track.album : ''}` : title}</p>

      {/* Album art */}
      <div className="mnp-art">
        {coverUrl ? <img className="mnp-art-img" src={coverUrl} alt={track ? track.title : 'cover'} /> : <FiMusic />}
        {track && !coverUrl && <span className="mnp-art-label">{track.title}</span>}
        {player.missing && <span className="mnp-art-missing">Audio not on this device</span>}
      </div>

      {/* Primary controls */}
      <div className="mnp-controls">
        <button className={`mnp-ctl ${player.repeat !== 'off' ? 'on' : ''}`} onClick={player.cycleRepeat}
          title={`Repeat: ${player.repeat}`}>
          <FiRepeat />{player.repeat === 'one' && <span className="mnp-ctl-badge">1</span>}
        </button>
        <button className="mnp-ctl" onClick={player.prev} disabled={!player.hasPrev && player.repeat !== 'all'} title="Previous"><FiSkipBack /></button>
        <button className="mnp-play" onClick={player.togglePlay} disabled={player.missing} title={player.isPlaying ? 'Pause' : 'Play'}>
          {player.isPlaying ? <FiPause /> : <FiPlay />}
        </button>
        <button className="mnp-ctl" onClick={player.next} disabled={!player.hasNext && player.repeat !== 'all' && !player.shuffle} title="Next"><FiSkipForward /></button>
        <button className={`mnp-ctl ${player.shuffle ? 'on' : ''}`} onClick={player.toggleShuffle} title="Shuffle"><FiShuffle /></button>
      </div>

      {/* Secondary actions */}
      <div className="mnp-actions">
        <button className={`mnp-act ${track && ratingOf(track) === 5 ? 'on' : ''}`} onClick={() => track && like(track)} disabled={!track} title="Like"><FiThumbsUp /></button>
        <button className={`mnp-act ${track && ratingOf(track) === 1 ? 'on' : ''}`} onClick={() => track && dislike(track)} disabled={!track} title="Dislike"><FiThumbsDown /></button>
        <button className={`mnp-act ${track && favOf(track) ? 'fav' : ''}`} onClick={() => track && fav(track)} disabled={!track} title="Favorite"><FiHeart /></button>
        <button className={`mnp-act ${showHelp ? 'on' : ''}`} onClick={() => setShowHelp(h => !h)} title="Help"><FiHelpCircle /></button>
        <button className={`mnp-act ${showList ? 'on' : ''}`} onClick={() => setShowList(s => !s)} title="Show list"><FiList /></button>
        <div className="mnp-vol">
          <button className="mnp-act" onClick={player.toggleMute} title="Mute"><VolIcon /></button>
          <input className="mnp-vol-range" type="range" min={0} max={1} step={0.01}
            value={player.muted ? 0 : player.volume} onChange={e => player.setVolume(Number(e.target.value))} />
        </div>
      </div>

      {/* Seek bar */}
      <div className="mnp-seek">
        <span className="mnp-time">{fmtTime(player.currentTime)}</span>
        <input className="mnp-seek-range" type="range" min={0} max={player.duration || 0} step={0.1}
          value={Math.min(player.currentTime, player.duration || 0)} onChange={e => player.seek(Number(e.target.value))}
          disabled={player.missing || !player.duration} />
        <span className="mnp-time">{fmtTime(player.duration)}</span>
      </div>

      {showHelp && (
        <div className="mnp-help">
          <strong>Controls:</strong> repeat (off → all → one) · previous · play/pause · next · shuffle ·
          👍 like · 👎 dislike · ♥ favorite · list toggle · volume. Click any song below to jump to it.
        </div>
      )}

      {/* Queue / playlist list */}
      {showList && (
        <div className="mnp-list">
          <div className="mnp-list-head">{title} · {queue.length} song{queue.length === 1 ? '' : 's'}</div>
          {queue.map((t, i) => (
            <div key={t.userOwnedId} className={`mnp-row ${i === index ? 'playing' : ''}`} onClick={() => setIndex(i)}>
              <span className="mnp-row-idx">{i === index && player.isPlaying ? '♪' : i + 1}</span>
              <div className="mnp-row-main">
                <span className="mnp-row-title">{t.title}</span>
                <span className="mnp-row-sub">{t.artist}{t.album ? ` · ${t.album}` : ''}</span>
              </div>
              {favOf(t) && <FiHeart className="mnp-row-fav" />}
              <span className="mnp-row-dur">{fmtTime(t.durationSeconds)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MusicNowPlaying;
